<template>
  <div class="pumping-lab d-flex flex-column">
    <!-- Skip Navigation Link -->
    <a href="#lab-main-content" class="skip-link">Skip to main content</a>
    
    <!-- Header -->
    <header
      class="lab-header d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between px-3 px-md-4 py-2 border-bottom bg-white gap-2"
    >
      <h5 class="mb-0 fw-bold">
        {{ appMeta.course }} &mdash; {{ appMeta.title }}
      </h5>
      <div
        class="app-utility-controls d-flex align-items-center gap-2 flex-wrap"
      >
        <button
          v-if="isDebugMode"
          class="btn btn-sm app-debug-btn app-debug-btn--warning"
          @click="loadPreset"
          title="Load debug preset for this scenario"
        >
          <i class="bi bi-lightning-fill me-1" aria-hidden="true"></i>Load
          Preset
        </button>
        <button
          v-if="isDebugMode"
          class="btn btn-sm app-debug-btn app-debug-btn--danger"
          @click="resetTours"
          title="Clear first-run tour flags for pre-lab and lab"
        >
          <i class="bi bi-arrow-counterclockwise me-1" aria-hidden="true"></i
          >Reset Tours
        </button>
        <button class="btn btn-sm btn-outline-primary" @click="startLabTour">
          <i class="bi bi-map me-1" aria-hidden="true"></i>Take Tour
        </button>
        <button
          class="btn btn-sm btn-outline-secondary"
          :aria-pressed="animationsPaused"
          :aria-label="
            animationsPaused
              ? 'Disable accessibility mode'
              : 'Enable accessibility mode'
          "
          @click="toggleAccessibilityMode"
        >
          <i
            class="bi me-1"
            :class="
              animationsPaused
                ? 'bi-universal-access-circle'
                : 'bi-universal-access'
            "
            aria-hidden="true"
          ></i>
          {{
            animationsPaused
              ? "Accessibility Mode: On"
              : "Accessibility Mode: Off"
          }}
        </button>
        <output
          v-if="resetToursMessage"
          class="small text-muted"
          aria-live="polite"
        >
          {{ resetToursMessage }}
        </output>
      </div>
    </header>

    <nav
      id="lab-tour-progress"
      class="app-progress-nav border-bottom bg-white px-3 px-md-4 py-3"
      aria-label="Application progress and navigation"
    >
      <div class="app-progress-layout">
        <div class="app-progress-context">
          <div class="app-progress-label mb-1">
            <i class="bi bi-compass-fill me-1" aria-hidden="true"></i>
            Application Progress Control
          </div>
          <p class="small text-muted mb-0">
            Use these controls to navigate and unlock all scenarios in the lab.
          </p>
        </div>

        <div class="app-progress-main">
          <div
            class="app-progress-controls d-flex align-items-center gap-2 flex-wrap"
          >
            <span class="fw-semibold small">
              Step {{ currentScenarioStep }} of {{ scenarios.length }}
            </span>
            <span class="scenario-name-label small">
              {{ activeScenario.fluidType || activeScenario.name }}
            </span>
            <span class="badge text-bg-light border">
              {{ completedScenarioCount }} / {{ scenarios.length }} Complete
            </span>
            <button
              class="btn btn-sm btn-outline-secondary"
              :disabled="!canGoPrev"
              @click="goToPreviousScenario"
            >
              Previous
            </button>
            <button
              class="btn btn-sm btn-primary"
              :disabled="!canGoNext"
              @click="goToNextScenario"
            >
              Next
            </button>
          </div>

          <div class="app-progress-bottom">
            <div
              class="scenario-stepper d-flex align-items-center gap-2 flex-wrap"
            >
              <button
                v-for="s in scenarios"
                :key="`step-${s.id}`"
                type="button"
                class="btn btn-sm"
                :class="stepButtonClass(s.id)"
                :disabled="isScenarioLocked(s.id)"
                :aria-label="`Go to Step ${s.id}`"
                :aria-current="s.id === selectedScenarioId ? 'step' : null"
                @click="goToScenarioStep(s.id)"
              >
                {{ s.id }}
              </button>
            </div>
            <output
              class="small text-muted app-progress-message"
              aria-live="polite"
            >
              {{ scenarioProgressMessage }}
            </output>
          </div>
        </div>
      </div>
    </nav>

    <!-- Requirements remain visible while lab stays available below -->
    <div id="lab-main-content" class="border-bottom bg-white p-3">
      <div id="lab-tour-requirements">
      <ScenarioRequirements
        :scenario="activeScenario"
        :fluidOptions="fluidOptions"
        @requirements-confirmed="onRequirementsConfirmed"
      />
      </div>
    </div>

    <!-- Lab Phase -->
    <div class="lab-canvas-container d-flex flex-column flex-lg-row">
      <!-- Left: SVG Canvas -->
      <div
        id="lab-tour-canvas"
        class="flex-grow-1 d-flex flex-column justify-content-center p-2 p-md-3 gap-3"
      >
        <PumpingDesignCanvas
          :scenario="activeScenario"
          :slots="generatedSlots"
          :slotAssignments="slotAssignments"
          :activeSlot="activeSlot"
          :simResult="simResult"
          :lastAssignedSlot="lastAssignedSlot"
          :animations-paused="animationsPaused"
          @select-slot="onSelectSlot"
          @clear-slot="onClearSlot"
          @clear-last-assigned="lastAssignedSlot = null"
        />
      </div>

      <!-- Right: Catalog Sidebar -->
      <aside
        id="lab-tour-catalog"
        class="catalog-sidebar border-top bg-white p-3 overflow-auto"
        aria-label="Component catalog"
      >
        <ComponentCatalog
          :catalog="catalog"
          :activeSlot="activeSlot"
          :activeSlotInfo="activeSlotInfo"
          :fluidKey="activeScenario.fluidKey"
          @assign-component="onAssignComponent"
        />
      </aside>
    </div>

    <!-- Bottom Cost Bar -->
    <div id="lab-tour-submit">
      <CostBar
        :totalCost="totalCost"
        :budget="activeScenario.budget"
        @submit-design="submitDesign"
        @back-to-prelab="goToPreLab"
      />
    </div>

    <!-- Feedback Section -->
    <div id="lab-tour-feedback">
      <DesignFeedback
        :submitted="designSubmitted"
        :visible="true"
        :totalCost="totalCost"
        :budget="activeScenario.budget"
        :componentCount="filledSlotCount"
        :components="placedComponents"
        :simResult="simResult"
        :validationResult="validationResult"
      />
    </div>
  </div>
</template>

<script>
import scenarios from "@/data/scenarios.json";
import catalog from "@/data/catalog.json";
import { getFluid } from "@/data/fluids.js";
import { evaluateDesign } from "@/utils/simulation.js";
import { validateDesign } from "@/utils/validator.js";
import { mapGetters, mapActions } from "vuex";
import config from "@/data/config.json";
import DatastoreAPI from "@/helpers/DatastoreAPI";
import { DebounceError } from "@/utils/debounce.js";
import { debugPresets } from "@/data/debugPresets.js";
import ScenarioRequirements from "@/components/activity/ScenarioRequirements.vue";
import ComponentCatalog from "@/components/activity/ComponentCatalog.vue";
import PumpingDesignCanvas from "@/components/activity/PumpingDesignCanvas.vue";
import CostBar from "@/components/activity/CostBar.vue";
import DesignFeedback from "@/components/activity/DesignFeedback.vue";
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";
import {
  buildShepherdSteps,
  hasSeenTour,
  markTourSeen,
  clearTourSeen
} from "@/utils/tour.js";

const LAB_TOUR_SEEN_KEY = "che334.labTourSeen.v1";
const PRELAB_TOUR_SEEN_KEY = "che334.prelabTourSeen.v1";
const ACCESSIBILITY_MODE_PREF_KEY = "che334.accessibilityMode.v1";

export default {
  name: "ActivityView",
  mixins: [DatastoreAPI],
  components: {
    ScenarioRequirements,
    ComponentCatalog,
    PumpingDesignCanvas,
    CostBar,
    DesignFeedback
  },
  data() {
    return {
      appMeta: config.meta,
      scenarios,
      catalog,
      lastAssignedSlot: null,
      tourDriver: null,
      hasAutoTourStarted: false,
      animationsPaused: false,
      resetToursMessage: ""
    };
  },
  watch: {
    mapToDatastore(val) {
      this.updateEntry(val).catch((e) => {
        if (!(e instanceof DebounceError)) console.error(e);
      });
    },
    selectedScenarioId: {
      immediate: true,
      handler() {
        this.normalizeSelectedScenario();
      }
    },
    scenarioCompletionMap: {
      handler() {
        this.normalizeSelectedScenario();
      }
    }
  },
  computed: {
    activeScenario() {
      return (
        this.scenarios.find((s) => s.id === this.selectedScenarioId) ||
        this.scenarios[0]
      );
    },
    fluidOptions() {
      return [...new Set(this.scenarios.map((s) => s.fluidType))].sort();
    },
    ...mapGetters([
      "selectedScenarioId",
      "slotAssignments",
      "activeSlot",
      "placedComponents",
      "totalCost",
      "filledSlotCount",
      "simResult",
      "validationResult",
      "designSubmitted",
      "requirementsConfirmed",
      "fittedCurve",
      "mapToDatastore",
      "isDebugMode"
    ]),
    generatedSlots() {
      const {
        pumps = 1,
        pipes = 1,
        valves = 1,
        fittings = 1
      } = this.activeScenario.slots || {};

      const slots = [];

      // Pipe 1 — between source tank and valve (horizontal)
      if (pipes >= 1) {
        slots.push({
          id: "pipe-1",
          label: "Pipe 1",
          accepts: "pipe",
          zone: "horizontal"
        });
      }

      // Valve(s)
      for (let i = 0; i < valves; i++) {
        slots.push({
          id: valves > 1 ? `valve-${i + 1}` : "valve",
          label: valves > 1 ? `Valve ${i + 1}` : "Valve",
          accepts: "valve",
          zone: "horizontal"
        });
      }

      // Pipe 2 — between valve and pump (horizontal)
      if (pipes >= 2) {
        slots.push({
          id: "pipe-2",
          label: "Pipe 2",
          accepts: "pipe",
          zone: "horizontal"
        });
      }

      // Pump(s)
      for (let i = 0; i < pumps; i++) {
        slots.push({
          id: pumps > 1 ? `pump-${i + 1}` : "pump",
          label: pumps > 1 ? `Pump ${i + 1}` : "Pump",
          accepts: "pump",
          zone: "horizontal"
        });
      }

      // Remaining pipes (3+) — discharge on the diagonal
      for (let i = 2; i < pipes; i++) {
        slots.push({
          id: `pipe-${i + 1}`,
          label: `Pipe ${i + 1}`,
          accepts: "pipe",
          zone: "diagonal"
        });
      }

      // Fittings — horizontal at the top (after diagonal)
      for (let i = 0; i < fittings; i++) {
        slots.push({
          id: `fitting-${i + 1}`,
          label: `Fitting ${i + 1}`,
          accepts: "fitting",
          zone: "top"
        });
      }

      return slots;
    },
    activeSlotInfo() {
      if (!this.activeSlot) return null;
      return this.generatedSlots.find((s) => s.id === this.activeSlot) || null;
    },
    scenarioCompletionMap() {
      const designs = this.$store.state.app?.scenarioDesigns || {};
      return this.scenarios.reduce((acc, scenario) => {
        const design = designs[scenario.id];
        const validationResult = design?.validationResult;
        let validationPassed = false;
        if (Array.isArray(validationResult)) {
          validationPassed = !validationResult.some(
            (result) => result?.severity === "hard"
          );
        } else if (Array.isArray(validationResult?.results)) {
          validationPassed = !validationResult.results.some(
            (result) => result?.severity === "hard"
          );
        } else {
          validationPassed = !!validationResult?.passed;
        }

        acc[scenario.id] = !!design?.designSubmitted && validationPassed;
        return acc;
      }, {});
    },
    completedScenarioCount() {
      return this.scenarios.filter((s) => this.scenarioCompletionMap[s.id])
        .length;
    },
    currentScenarioStep() {
      const currentIndex = this.scenarios.findIndex(
        (s) => s.id === this.selectedScenarioId
      );
      return currentIndex >= 0 ? currentIndex + 1 : 1;
    },
    nextScenarioId() {
      const currentIndex = this.scenarios.findIndex(
        (s) => s.id === this.selectedScenarioId
      );
      if (currentIndex < 0 || currentIndex >= this.scenarios.length - 1) {
        return null;
      }
      return this.scenarios[currentIndex + 1].id;
    },
    previousScenarioId() {
      const currentIndex = this.scenarios.findIndex(
        (s) => s.id === this.selectedScenarioId
      );
      if (currentIndex <= 0) return null;
      return this.scenarios[currentIndex - 1].id;
    },
    firstUnlockedScenarioId() {
      const firstUnlocked = this.scenarios.find(
        (s) => !this.isScenarioLocked(s.id)
      );
      return firstUnlocked ? firstUnlocked.id : this.scenarios[0]?.id || null;
    },
    canGoPrev() {
      return !!this.previousScenarioId;
    },
    canGoNext() {
      return (
        !!this.nextScenarioId &&
        !this.isScenarioLocked(this.nextScenarioId) &&
        this.scenarioCompletionMap[this.selectedScenarioId]
      );
    },
    scenarioProgressMessage() {
      if (this.completedScenarioCount === this.scenarios.length) {
        return "All scenarios are complete.";
      }

      const firstIncomplete = this.scenarios.find(
        (s) => !this.scenarioCompletionMap[s.id]
      );

      if (
        this.scenarioCompletionMap[this.selectedScenarioId] &&
        this.nextScenarioId &&
        !this.isScenarioLocked(this.nextScenarioId)
      ) {
        return `Step ${this.currentScenarioStep} complete. Continue to Step ${this.currentScenarioStep + 1}.`;
      }

      if (firstIncomplete) {
        return `Complete Step ${firstIncomplete.id} to unlock the next step.`;
      }

      return "";
    }
  },
  methods: {
    ...mapActions([
      "setScenario",
      "setActiveSlot",
      "assignSlot",
      "clearSlot",
      "submitDesignResult",
      "setRequirementsConfirmed"
    ]),
    handleStorageError(action, error) {
      // Non-blocking: storage access can fail in private mode or restricted browsers.
      console.warn(`Failed to ${action} accessibility preference.`, error);
    },
    prefersReducedMotion() {
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    },
    restoreAnimationsPreference() {
      try {
        const stored = window.localStorage.getItem(ACCESSIBILITY_MODE_PREF_KEY);
        this.animationsPaused =
          stored === null ? this.prefersReducedMotion() : stored === "true";
      } catch (e) {
        this.handleStorageError("read", e);
        this.animationsPaused = this.prefersReducedMotion();
      }
    },
    persistAnimationsPreference() {
      try {
        window.localStorage.setItem(
          ACCESSIBILITY_MODE_PREF_KEY,
          String(this.animationsPaused)
        );
      } catch (e) {
        this.handleStorageError("save", e);
      }
    },
    toggleAccessibilityMode() {
      this.animationsPaused = !this.animationsPaused;
      this.persistAnimationsPreference();
    },
    startLabTour() {
      this.tourDriver?.cancel();
      this.tourDriver = new Shepherd.Tour({
        useModalOverlay: true,
        exitOnEsc: true,
        keyboardNavigation: true,
        defaultStepOptions: {
          canClickTarget: false,
          cancelIcon: { enabled: true },
          scrollTo: { behavior: "smooth", block: "center" }
        }
      });

      const steps = buildShepherdSteps(
        [
          {
            selector: "#lab-tour-progress",
            title: "Application Progress",
            description:
              "Use this control bar to navigate between scenarios and track global completion."
          },
          {
            selector: "#lab-tour-requirements",
            title: "Scenario Requirements",
            description:
              "Read the scenario and confirm all requirement fields before final submission."
          },
          {
            selector: "#lab-tour-canvas",
            title: "Design Canvas",
            description:
              "Build your system here by placing pumps, pipes, valves, and fittings into slots."
          },
          {
            selector: "#lab-tour-catalog",
            title: "Component Catalog",
            description:
              "Select components from the catalog and assign them to compatible slots in your design."
          },
          {
            selector: "#lab-tour-submit",
            title: "Submit Design",
            description:
              "Review cost and submit your design when requirements are met."
          },
          {
            selector: "#lab-tour-feedback",
            title: "Feedback",
            description:
              "Inspect validation and performance feedback to improve and iterate your design."
          }
        ],
        this.tourDriver
      );

      if (steps.length === 0) {
        this.tourDriver.cancel();
        return;
      }

      this.tourDriver.addSteps(steps);
      this.tourDriver.start();
    },
    maybeStartLabTourOnce() {
      if (this.hasAutoTourStarted) return;
      if (hasSeenTour(LAB_TOUR_SEEN_KEY)) return;

      this.hasAutoTourStarted = true;
      this.markLabTourSeen();
      this.$nextTick(() => {
        this.startLabTour();
      });
    },
    markLabTourSeen() {
      markTourSeen(LAB_TOUR_SEEN_KEY);
    },
    resetTours() {
      clearTourSeen(LAB_TOUR_SEEN_KEY, PRELAB_TOUR_SEEN_KEY);
      this.hasAutoTourStarted = false;
      this.resetToursMessage =
        "Tours reset. Reload or revisit pages to see first-run tours again.";
    },
    normalizeSelectedScenario() {
      if (this.isDebugMode) return;
      if (
        !this.selectedScenarioId ||
        !this.isScenarioLocked(this.selectedScenarioId)
      ) {
        return;
      }
      if (
        this.firstUnlockedScenarioId &&
        this.firstUnlockedScenarioId !== this.selectedScenarioId
      ) {
        this.setScenario(this.firstUnlockedScenarioId);
      }
    },
    isScenarioLocked(scenarioId) {
      if (this.isDebugMode) return false;
      const scenarioIndex = this.scenarios.findIndex(
        (s) => s.id === scenarioId
      );
      if (scenarioIndex <= 0) return false;

      for (let i = 0; i < scenarioIndex; i++) {
        const prevScenarioId = this.scenarios[i].id;
        if (!this.scenarioCompletionMap[prevScenarioId]) {
          return true;
        }
      }
      return false;
    },
    stepButtonClass(scenarioId) {
      if (scenarioId === this.selectedScenarioId) return "btn-primary";
      if (this.scenarioCompletionMap[scenarioId]) return "btn-success";
      if (this.isScenarioLocked(scenarioId)) return "btn-outline-secondary";
      return "btn-outline-primary";
    },
    goToScenarioStep(scenarioId) {
      if (this.isScenarioLocked(scenarioId)) return;
      this.setScenario(scenarioId);
    },
    goToPreviousScenario() {
      if (!this.previousScenarioId) return;
      this.setScenario(this.previousScenarioId);
    },
    goToNextScenario() {
      if (!this.canGoNext) {
        return;
      }
      this.setScenario(this.nextScenarioId);
    },
    goToPreLab() {
      this.$router.push({ name: "PreLab" });
    },
    onRequirementsConfirmed() {
      this.setRequirementsConfirmed(true);
    },
    onSelectSlot(slotId) {
      this.setActiveSlot(slotId);
    },
    onAssignComponent({ slotId, component }) {
      let targetSlotId = slotId;
      if (!targetSlotId) {
        // Auto-find the first unfilled slot that accepts this component category
        const emptyMatch = this.generatedSlots.find(
          (s) => s.accepts === component.category && !this.slotAssignments[s.id]
        );
        if (!emptyMatch) {
          window.alert(
            `All ${component.category} slots are already filled. Remove one before adding another.`
          );
          return;
        }
        targetSlotId = emptyMatch.id;
      }
      this.assignSlot({ slotId: targetSlotId, component });
      this.setActiveSlot(null);
      // Trigger the pop-in animation on the assigned slot
      this.lastAssignedSlot = targetSlotId;
    },
    onClearSlot(slotId) {
      this.clearSlot(slotId);
    },
    loadPreset() {
      const preset = debugPresets[this.selectedScenarioId];
      if (!preset) return;
      this.$store.dispatch("clearDesign");
      this.setRequirementsConfirmed(true);
      Object.entries(preset).forEach(([slotId, component]) => {
        this.assignSlot({ slotId, component });
      });
    },
    submitDesign() {
      const components = this.placedComponents;
      const scenario = this.activeScenario;
      const fluid = getFluid(scenario.fluidKey);

      // Find the first placed pipe and pump for simulation
      const pipeComp = components.find((c) => c.category === "pipe");
      const pumpComp = components.find((c) => c.category === "pump");

      // Sum K-values from all fittings and valves
      const Ktotal = components
        .filter((c) => c.category === "fitting" || c.category === "valve")
        .reduce((sum, c) => sum + (c.K || 0), 0);

      // Sum pipe lengths from all placed pipe segments
      const totalPipeLength_m = components
        .filter((c) => c.category === "pipe")
        .reduce((sum, c) => sum + (c.length_m || 0), 0);

      // Run simulation if we have the minimum required components
      let simResult = null;
      if (pipeComp && pumpComp && fluid) {
        // Build pipe object with selectedSize for the simulation engine
        const selectedSize =
          pipeComp.selectedSize ||
          (pipeComp.sizes && pipeComp.sizes[0]) ||
          null;
        if (selectedSize) {
          // Use sum of actual placed pipe lengths, fall back to scenario default
          const overrideScenario = {
            ...scenario,
            pipeRunLength_m: totalPipeLength_m || scenario.pipeRunLength_m
          };
          // Use the student's pre-lab fitted curve if available,
          // otherwise fall back to the catalog pump's built-in curve.
          const effectivePump = this.fittedCurve
            ? { ...pumpComp, curve: this.fittedCurve }
            : pumpComp;

          simResult = evaluateDesign({
            fluid,
            scenario: overrideScenario,
            pipe: { ...pipeComp, selectedSize },
            Ktotal,
            pump: effectivePump
          });
        }
      }

      // Run validator
      const validationResult = validateDesign({
        components,
        scenario,
        simResult,
        totalCost: this.totalCost
      });

      this.submitDesignResult({
        simResult,
        validationResult
      });
    }
  },
  created() {
    this.restoreAnimationsPreference();
  },
  mounted() {
    this.maybeStartLabTourOnce();
  },
  beforeUnmount() {
    this.tourDriver?.cancel();
  }
};
</script>

<style scoped>
.pumping-lab {
  min-height: 100svh;
}

.lab-canvas-container {
  min-height: 55vh;
}

.catalog-sidebar {
  flex: 0 0 340px;
  width: 340px;
  max-width: 100%;
  max-height: 72vh;
  overflow-y: auto;
}

.scenario-stepper .btn {
  min-width: 2.25rem;
}

.app-progress-layout {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.app-progress-context {
  min-width: 0;
}

.app-progress-main {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.app-progress-controls {
  justify-content: flex-start;
}

.app-utility-controls {
  justify-content: flex-start;
}

.app-debug-btn {
  font-weight: 600;
}

.app-debug-btn--warning {
  color: #7a4e00;
  border-color: #7a4e00;
  background-color: #fff;
}

.app-debug-btn--warning:hover,
.app-debug-btn--warning:focus-visible {
  color: #fff;
  background-color: #7a4e00;
  border-color: #7a4e00;
}

.app-debug-btn--warning:focus-visible {
  box-shadow: 0 0 0 0.2rem rgba(122, 78, 0, 0.25);
}

.app-debug-btn--danger {
  color: #b02a37;
  border-color: #b02a37;
  background-color: #fff;
}

.app-debug-btn--danger:hover,
.app-debug-btn--danger:focus-visible {
  color: #fff;
  background-color: #b02a37;
  border-color: #b02a37;
}

.app-debug-btn--danger:focus-visible {
  box-shadow: 0 0 0 0.2rem rgba(176, 42, 55, 0.25);
}

.app-progress-bottom {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
  flex-wrap: wrap;
}

.app-progress-message {
  margin: 0;
}

.app-progress-label {
  color: #0b5ed7;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.scenario-name-label {
  color: #0b3d91;
  font-weight: 700;
  letter-spacing: 0.01em;
}

@media (max-width: 991.98px) {
  .catalog-sidebar {
    width: 100%;
    flex-basis: auto;
    max-height: 45vh;
  }
}

@media (min-width: 992px) {
  .app-progress-layout {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .app-progress-context {
    flex: 0 0 340px;
    max-width: 420px;
  }

  .app-progress-main {
    flex: 1;
    min-width: 0;
  }

  .app-progress-controls {
    justify-content: flex-end;
  }

  .app-utility-controls {
    justify-content: flex-end;
  }

  .app-progress-bottom {
    justify-content: flex-end;
  }

  .app-progress-message {
    text-align: right;
  }
}
</style>
